import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { valisightFetch, isValisightProxyEnabled } from './valisightClient';
import { buildRulePayload, describeTestResult } from './alertRulesLogic';

/**
 * #261 P3 / #113 — Alert-Regel-Verwaltung im Orbit-Admin. Reale Anbindung an
 * die valisight-Routen unter `/proxy/valisight`:
 *   GET    /v1/alert-rules?tenantId=&sourceId=   (Liste, Secrets maskiert)
 *   POST   /v1/alert-rules                        (anlegen)
 *   PATCH  /v1/alert-rules/:id  { enabled }       (aktivieren/deaktivieren)
 *   POST   /v1/alert-rules/:id/test               (Test-Alarm senden)
 *   DELETE /v1/alert-rules/:id                    (löschen)
 * Alle liegen hinter dem gateway-guard; der BFF hängt das Gateway-Secret an.
 *
 * Der Live-Payload wird über `buildRulePayload` (unit-getestet) gebaut, das die
 * kanal-spezifische Validierung des Backends spiegelt — ungültige Eingaben
 * werfen hier, bevor ein Request rausgeht.
 */

// Regeln laden. Ohne tenantId listet das Backend die tenant-weiten Regeln
// (tenant_id IS NULL). Für einen konkreten Tenant den Filter mitschicken.
export function useValisightAlertRules({ tenantId = '', sourceId = '' } = {}) {
  const params = new URLSearchParams();
  if (tenantId !== '') params.set('tenantId', String(tenantId));
  if (sourceId !== '') params.set('sourceId', String(sourceId));
  const qs = params.toString();
  return useQuery({
    queryKey: ['vs-alert-rules', tenantId, sourceId],
    queryFn: () => valisightFetch(`/v1/alert-rules${qs ? `?${qs}` : ''}`),
    enabled: isValisightProxyEnabled(),
    retry: false,
    refetchInterval: 60_000,
  });
}

export function useCreateAlertRule() {
  return useApiMutation({
    live: (_fetch, form) => {
      // Client-seitig validieren (spiegelt das Backend-Schema), damit der
      // Operator kanal-spezifische Fehler ohne 400-Roundtrip sieht.
      const built = buildRulePayload(form);
      if (!built.ok) return Promise.reject(new Error(built.error));
      return valisightFetch('/v1/alert-rules', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: built.payload,
      });
    },
    invalidate: ['vs-alert-rules'],
    successMessage: () => 'Alert-Regel angelegt.',
  });
}

export function useToggleAlertRule() {
  return useApiMutation({
    // tenantId muss mit, weil das Backend das Update auf den Tenant scoped
    // (tenant_id IS NOT DISTINCT FROM ?) — sonst greift die Regel nicht.
    live: (_fetch, { id, enabled, tenantId }) =>
      valisightFetch(`/v1/alert-rules/${encodeURIComponent(id)}`, {
        method: 'PATCH', headers: { 'content-type': 'application/json' },
        body: { enabled, ...(tenantId != null && tenantId !== '' ? { tenantId: Number(tenantId) } : {}) },
      }),
    invalidate: ['vs-alert-rules'],
    successMessage: (_res, { enabled }) => (enabled ? 'Regel aktiviert.' : 'Regel deaktiviert.'),
  });
}

export function useTestAlertRule() {
  return useApiMutation({
    live: (_fetch, { id, tenantId }) =>
      valisightFetch(`/v1/alert-rules/${encodeURIComponent(id)}/test`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: tenantId != null && tenantId !== '' ? { tenantId: Number(tenantId) } : {},
      }),
    // Der Test-Endpoint liefert { ok, error } auch bei erfolglosem Kanal (HTTP
    // 200) → den fachlichen Ausgang als Toast aufbereiten.
    successMessage: (res) => describeTestResult(res),
  });
}

export function useDeleteAlertRule() {
  return useApiMutation({
    live: (_fetch, { id, tenantId }) => {
      const params = new URLSearchParams();
      if (tenantId != null && tenantId !== '') params.set('tenantId', String(tenantId));
      const qs = params.toString();
      return valisightFetch(`/v1/alert-rules/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
    },
    invalidate: ['vs-alert-rules'],
    successMessage: () => 'Regel gelöscht.',
  });
}
